import { useI18N } from '@susu/i18n/client'
import { ActionCard } from '@susu/ui/components'

import { API_BASE_URLS } from '../config/api.config'
import { useCustomerHub } from '../contexts/CustomerHubContext'

import type { CustomerStatus } from './CustomerHubCore'

interface CustomerTimelineProps {
  token: string
  customerEmail: string
  accountNumber: string
  locationId: string
  env: 'tst' | 'acc' | 'prd'
  customerStatus?: CustomerStatus
}

interface CustomerTimelineUrlParams extends CustomerTimelineProps {
  context?: string
  currentUrl: string
}

export function buildCustomerTimelineUrl({
  token,
  customerEmail,
  accountNumber,
  locationId,
  env,
  customerStatus,
  context,
  currentUrl,
}: CustomerTimelineUrlParams): string {
  const isStandaloneContext = context === 'standalone'
  const redirectUri = new URL(currentUrl)

  if (isStandaloneContext) {
    redirectUri.searchParams.set('customerProfileEmail', customerEmail)
  } else {
    redirectUri.searchParams.set('locationId', locationId)
    redirectUri.searchParams.set('CustomerID', accountNumber)
    redirectUri.searchParams.set('customerSearchToken', token)
    redirectUri.searchParams.set('env', env)

    if (customerStatus) {
      redirectUri.searchParams.set('customerProfileStatus', customerStatus)
    } else {
      redirectUri.searchParams.delete('customerProfileStatus')
    }
  }

  const redirectUrl = encodeURIComponent(redirectUri.toString())
  const encodedAccountNumber = encodeURIComponent(accountNumber)
  const encodedToken = encodeURIComponent(token)

  return `${API_BASE_URLS[env]}/public/legacytimeline/profile?type=timeline&showNavigation=true&backNavigationRedirectUri=${redirectUrl}&accountNumber=${encodedAccountNumber}&access_token=${encodedToken}`
}

export default function CustomerTimeline({
  token,
  customerEmail,
  accountNumber,
  locationId,
  env,
  customerStatus,
}: CustomerTimelineProps) {
  const { context } = useCustomerHub()
  const { t } = useI18N()

  const handleClick = () => {
    const url = buildCustomerTimelineUrl({
      token,
      customerEmail,
      accountNumber,
      locationId,
      env,
      customerStatus,
      context,
      currentUrl: window.location.href,
    })
    window.location.href = url
  }

  return (
    <div className="flex flex-col max-lg:px-16">
      <h2 className="text-xl leading-xl font-medium text-text mb-16">
        {t('timeline.heading')}
      </h2>
      <ActionCard
        title={t('timeline.cardTitle')}
        description={t('timeline.cardDescription')}
        leadingIcon={'calendar'}
        showTrailingIcon={false}
        onClick={handleClick}
      />
    </div>
  )
}
